import { pct } from "../lib/calc";
import { cleanPrintLabel } from "../lib/printLabel";
import type { MarketProduct } from "../types";

type BarProps = {
  youMonthly: number;
  mktMonthly: number;
  youLabel: string;
  mktLabel: string;
  youMoney: string;
  mktMoney: string;
};

export function PayBars({ youMonthly, mktMonthly, youLabel, mktLabel, youMoney, mktMoney }: BarProps) {
  const max = Math.max(youMonthly, mktMonthly, 1);
  return (
    <div className="bars" role="img" aria-label={`Monthly repayments. You ${youMoney}, market ${mktMoney}.`}>
      <div className="bar-row">
        <span>{youLabel}</span>
        <div className="track">
          <i className="you" style={{ width: `${(youMonthly / max) * 100}%` }} />
        </div>
        <b>{youMoney}</b>
      </div>
      <div className="bar-row">
        <span>{mktLabel}</span>
        <div className="track">
          <i className="mkt" style={{ width: `${(mktMonthly / max) * 100}%` }} />
        </div>
        <b>{mktMoney}</b>
      </div>
    </div>
  );
}

type StripProps = {
  youRate: number;
  youLabel: string;
  products: MarketProduct[];
  selected: MarketProduct;
  onSelect: (product: MarketProduct) => void;
};

function shortProduct(product: string): string {
  return cleanPrintLabel(product)
    .replace(/\s+\d.*$/, "")
    .replace(/\s+(<=)?\d+(-\d+)?%$/i, "")
    .trim();
}

export function RateStrip({ youRate, youLabel, products, selected, onSelect }: StripProps) {
  const rates = [youRate, ...products.map((p) => p.rate)];
  const min = Math.min(...rates) - 0.08;
  const max = Math.max(...rates) + 0.08;
  const span = Math.max(0.25, max - min);
  const at = (rate: number) => `${((rate - min) / span) * 100}%`;
  const youAt = at(youRate);

  return (
    <figure className="rate-stave">
      <figcaption>
        <span>Cited prints on this file</span>
        <span className="rate-stave-axis" aria-hidden>
          <em>cheaper</em>
          <em>dearer</em>
        </span>
      </figcaption>
      <ol className="rate-stave-rows">
        <li className="is-you">
          <div className="rate-stave-who">
            <b>You</b>
            <small>{youLabel}</small>
          </div>
          <div className="rate-stave-lane" role="img" aria-label={`You ${pct(youRate)}`}>
            <i className="rate-stave-fill is-you" style={{ width: youAt }} />
            <i className="rate-stave-peg is-you" style={{ left: youAt }} />
          </div>
          <strong>{pct(youRate)}</strong>
        </li>
        {products.map((p) => {
          const on = p.lender === selected.lender && p.product === selected.product;
          const mark = at(p.rate);
          return (
            <li key={`${p.lender}-${p.product}`} className={on ? "is-on" : undefined}>
              <button type="button" aria-pressed={on} onClick={() => onSelect(p)}>
                <span className="rate-stave-who">
                  <b>{p.lender}</b>
                  <small>{shortProduct(p.product)}</small>
                </span>
                <span className="rate-stave-lane" aria-hidden>
                  <i className="rate-stave-you-mark" style={{ left: youAt }} />
                  <i className={`rate-stave-fill ${on ? "is-on" : "is-mkt"}`} style={{ width: mark }} />
                  <i className={`rate-stave-peg ${on ? "is-on" : "is-mkt"}`} style={{ left: mark }} />
                </span>
                <strong>{pct(p.rate)}</strong>
              </button>
            </li>
          );
        })}
      </ol>
    </figure>
  );
}
